import { useState } from "react";

import {
    Quest,
    UpdateQuest,
    QUEST_DIFFICULTY,
    Objective,
    Battle,
    BATTLE_STATUS,
    BATTLE_STATUS_NAME,
} from "../../interfaces";

import "../../styles/questview.css";
import { ObjectiveEntry, BattleEntry } from "../molecules";
import QuestEdit from "./QuestEdit";
import { Button } from "../atoms";
import { textGradient, COLOR, gradient } from "../../common/css/colors";

interface QuestViewProps {
    quest: Quest;
    deleteQuest: () => void;
    updateQuest: (updates: UpdateQuest) => void;
    addObjective: (objective: Objective) => void;
    addBattle: (battle: Battle) => void;
    removeBattle: (battleId: string) => void;
    completeQuest: () => void;
}

const QuestView = ({
    quest,
    deleteQuest,
    updateQuest,
    addObjective,
    addBattle,
    removeBattle,
    completeQuest,
}: QuestViewProps) => {
    const [editing, setEditing] = useState(false);
    const [workingQuest, setWorkingQuest] = useState(quest);
    const [recordingBattle, setRecordingBattle] = useState(false);

    const toggleEditing = () => {
        setEditing((p) => !p);
        setWorkingQuest(quest);
    };

    const submitEdit = () => {
        const updates = {} as UpdateQuest;

        if (workingQuest.title !== quest.title)
            updates.title = workingQuest.title;
        if (workingQuest.desc !== quest.desc) updates.desc = workingQuest.desc;
        if (workingQuest.dateStarted !== quest.dateStarted)
            updates.dateStarted = workingQuest.dateStarted;
        if (workingQuest.dateFinished !== quest.dateFinished)
            updates.dateFinished = workingQuest.dateFinished;
        if (workingQuest.intendedEndDate !== quest.intendedEndDate)
            updates.intendedEndDate = workingQuest.intendedEndDate;
        if (workingQuest.difficulty !== quest.difficulty)
            updates.difficulty = workingQuest.difficulty;

        updateQuest(updates);
        toggleEditing();
    };

    let titleGradient: Record<string, string> = {};
    switch (quest.difficulty) {
        case QUEST_DIFFICULTY.GRAY:
            titleGradient = textGradient(COLOR.pink5, COLOR.blue1);
            break;
        case QUEST_DIFFICULTY.GREEN:
            titleGradient = textGradient(COLOR.green, COLOR.lime1);
            break;
        case QUEST_DIFFICULTY.YELLOW:
            titleGradient = textGradient(COLOR.orange, COLOR.yellow);
            break;
        case QUEST_DIFFICULTY.ORANGE:
            titleGradient = textGradient(COLOR.orange, COLOR.red2);
            break;
        case QUEST_DIFFICULTY.RED:
            titleGradient = textGradient(COLOR.red3, COLOR.red5);
            break;
    }

    const _addObjective = (objective: Objective) => {
        addObjective(objective);
        setWorkingQuest((p) => ({
            ...p,
            objectives: [...p.objectives, objective],
        }));
    };

    return (
        <>
            {editing ? (
                <QuestEdit
                    quest={workingQuest}
                    setQuest={setWorkingQuest}
                    submitObjective={_addObjective}
                    submit={submitEdit}
                    cancel={() => toggleEditing()}
                />
            ) : (
                <div className="qviewWrapper">
                    <div className="qviewInner">
                        <div className="title" style={titleGradient}>
                            {quest.title}
                        </div>
                        <div className="desc">{quest.desc}</div>
                        <div>Started: {quest.dateStarted}</div>

                        {quest.objectives.length > 0 && (
                            <h3 style={{ marginTop: "0.5em" }}>Objectives</h3>
                        )}
                        {quest.objectives.map((obj, i) => (
                            <ObjectiveEntry key={i} objective={obj} />
                        ))}

                        {quest.battles.length > 0 && (
                            <>
                                <h3 style={{ marginTop: "0.5em" }}>
                                    Battle log
                                </h3>
                                {quest.battles
                                    .sort((a, b) =>
                                        a.dateOf < b.dateOf ? 1 : -1,
                                    )
                                    .map((battle, i) => (
                                        <BattleEntry
                                            key={i}
                                            battle={battle}
                                            remove={() =>
                                                removeBattle(battle.id)
                                            }
                                        />
                                    ))}
                            </>
                        )}

                        <div className="btnGroup">
                            <Button
                                style={{ marginRight: "1em" }}
                                text="Record battle"
                                onClick={() => setRecordingBattle((p) => !p)}
                            />
                            {!editing && (
                                <Button
                                    style={{ marginRight: "1em" }}
                                    text="Edit"
                                    onClick={toggleEditing}
                                />
                            )}
                            <Button
                                style={{ marginRight: "1em" }}
                                text="Delete"
                                onClick={deleteQuest}
                            />
                            <Button
                                text="Complete"
                                onClick={completeQuest}
                                background={
                                    gradient(COLOR.purp1, COLOR.peach3)
                                        .background
                                }
                            />
                        </div>

                        {recordingBattle && (
                            <BattleEdit
                                submit={addBattle}
                                cancel={() => setRecordingBattle(false)}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default QuestView;

const BattleEdit = ({
    submit,
    cancel,
}: {
    submit: (x: Battle) => void;
    cancel: () => void;
}) => {
    const [status, setStatus] = useState(BATTLE_STATUS.VICTORY);
    const [dateOf, setDateOf] = useState("");
    const [timeOf, setTimeOf] = useState("");
    const [desc, setDesc] = useState("");

    const handleSubmit = () => {
        if (!dateOf.length) {
            console.error("'dateOf' can't be empty");
            return;
        }

        const _dateOf = new Date(
            dateOf + " " + (timeOf.length ? timeOf : "12:00"),
        );

        const _dateOfComps = _dateOf.toISOString().replace("Z", "").split("T");
        submit({
            id: "",
            dateCreated: "",
            dateUpdated: "",
            status,
            dateOf: `${_dateOfComps[0]} ${_dateOfComps[1]}`,
            desc,
        });
        cancel();
    };

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "auto auto auto auto",
            }}
        >
            <h4>Status</h4>
            <h4>Date</h4>
            <h4>Time</h4>
            <h4>Description</h4>

            <select
                value={status}
                onChange={(e) => setStatus(Number(e.target.value))}
            >
                <option value={BATTLE_STATUS.VICTORY}>
                    {BATTLE_STATUS_NAME[BATTLE_STATUS.VICTORY]}
                </option>
                <option value={BATTLE_STATUS.ONGOING}>
                    {BATTLE_STATUS_NAME[BATTLE_STATUS.ONGOING]}
                </option>
                <option value={BATTLE_STATUS.DEFEAT}>
                    {BATTLE_STATUS_NAME[BATTLE_STATUS.DEFEAT]}
                </option>
            </select>
            <input
                type="date"
                value={dateOf}
                onChange={(e) => setDateOf(e.target.value)}
            />
            <input
                type="time"
                value={timeOf}
                onChange={(e) => setTimeOf(e.target.value)}
            />
            <input
                type="text"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
            />

            <div
                style={{
                    gridColumnStart: "4",
                    gridColumnEnd: "5",
                    display: "flex",
                    justifyContent: "end",
                    paddingTop: "1em",
                }}
            >
                <Button
                    style={{ marginRight: "1em" }}
                    text="Submit"
                    onClick={() => handleSubmit()}
                />
                <Button text="Cancel" onClick={() => cancel()} />
            </div>
        </div>
    );
};
